(() => {
  if (window.__vivaPdpAccordionPreview) return;
  window.__vivaPdpAccordionPreview = true;

  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const wrapperSelector = '[data-pdp-accordion-preview]';
  const contentSelector = '.accordion__content';
  const buttonSelector = '.accordion__content-toggle';
  const singleOpenRootSelector = '[data-single-open-accordion="true"]';
  const accordionDetailsSelector = '.product__accordion > .accordion__details';
  const accordionSummarySelector = '.accordion__summary';
  const previewTolerance = 8;
  const wrapperTransitionLocks = new WeakMap();
  let scrollAnchorLockDepth = 0;
  let rootOverflowAnchorBeforeLocks = '';

  const setButtonLabel = (button, expanded) => {
    const label = expanded ? button.dataset.showLessLabel : button.dataset.showMoreLabel;
    const labelTarget = button.querySelector('span') || button;
    labelTarget.textContent = label;
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  };

  const getStickyHeaderOffset = () => {
    const header = document.querySelector('.shopify-section-header-sticky, sticky-header');
    if (!header) return 0;

    const rect = header.getBoundingClientRect();
    return rect.top <= 0 && rect.bottom > 0 ? rect.height : 0;
  };

  const withInstantWindowScroll = (callback) => {
    const root = document.documentElement;
    const body = document.body;
    const rootScrollBehavior = root.style.scrollBehavior;
    const bodyScrollBehavior = body.style.scrollBehavior;

    root.style.scrollBehavior = 'auto';
    body.style.scrollBehavior = 'auto';
    try {
      callback();
    } finally {
      root.style.scrollBehavior = rootScrollBehavior;
      body.style.scrollBehavior = bodyScrollBehavior;
    }
  };

  const suppressWrapperTransition = (wrapper) => {
    let lock = wrapperTransitionLocks.get(wrapper);
    if (!lock) {
      lock = { depth: 0, transition: wrapper.style.transition };
      wrapperTransitionLocks.set(wrapper, lock);
    }

    lock.depth += 1;
    wrapper.style.transition = 'none';

    return () => {
      lock.depth -= 1;
      if (lock.depth > 0) return;

      wrapper.style.transition = lock.transition;
      wrapperTransitionLocks.delete(wrapper);
    };
  };

  const suppressRootScrollAnchoring = () => {
    const root = document.documentElement;
    if (scrollAnchorLockDepth === 0) {
      rootOverflowAnchorBeforeLocks = root.style.overflowAnchor;
      root.style.overflowAnchor = 'none';
    }
    scrollAnchorLockDepth += 1;

    return () => {
      scrollAnchorLockDepth -= 1;
      if (scrollAnchorLockDepth === 0) {
        root.style.overflowAnchor = rootOverflowAnchorBeforeLocks;
      }
    };
  };

  const scrollCollapsedWrapperIntoView = (wrapper) => {
    const accordion = wrapper.closest('.product__accordion') || wrapper;
    requestAnimationFrame(() => {
      const headerOffset = getStickyHeaderOffset();
      const top = accordion.getBoundingClientRect().top + window.scrollY - headerOffset - 12;
      const currentTop = accordion.getBoundingClientRect().top;

      if (currentTop < headerOffset + 12) {
        withInstantWindowScroll(() => window.scrollTo(0, Math.max(0, top)));
      }
    });
  };

  const closeOtherAccordions = (details, accordionDetails, summaryTop) => {
    const openDetails = accordionDetails.filter(
      (otherDetails) => otherDetails !== details && otherDetails.open
    );
    if (!openDetails.length) return;

    if (!Number.isFinite(summaryTop)) {
      openDetails.forEach((otherDetails) => {
        otherDetails.open = false;
      });
      return;
    }

    const summary = details.querySelector(accordionSummarySelector);
    const restoreScrollAnchoring = suppressRootScrollAnchoring();
    const restoreWrapperTransitions = openDetails
      .map((otherDetails) => otherDetails.nextElementSibling)
      .filter(Boolean)
      .map(suppressWrapperTransition);

    try {
      openDetails.forEach((otherDetails) => {
        otherDetails.open = false;
      });

      if (summary) {
        const scrollDelta = summary.getBoundingClientRect().top - summaryTop;
        if (Math.abs(scrollDelta) > 0.5) {
          withInstantWindowScroll(() => window.scrollBy(0, scrollDelta));
        }
      }
    } finally {
      requestAnimationFrame(() => {
        restoreWrapperTransitions.forEach((restoreTransition) => restoreTransition());
        restoreScrollAnchoring();
      });
    }
  };

  const initSingleOpenAccordion = (root) => {
    if (root.dataset.singleOpenAccordionInitialized === 'true') return;

    const accordionDetails = Array.from(root.querySelectorAll(accordionDetailsSelector));
    if (!accordionDetails.length) return;

    root.dataset.singleOpenAccordionInitialized = 'true';

    let hasOpenAccordion = false;
    accordionDetails.forEach((details) => {
      if (details.open) {
        if (hasOpenAccordion) {
          details.open = false;
        } else {
          hasOpenAccordion = true;
        }
      }

      const summary = details.querySelector(accordionSummarySelector);
      let activatedSummaryTop = null;

      if (summary) {
        summary.addEventListener('click', () => {
          activatedSummaryTop = summary.getBoundingClientRect().top;
        });
      }

      details.addEventListener('toggle', () => {
        const summaryTop = activatedSummaryTop;
        activatedSummaryTop = null;
        if (!details.open) return;

        closeOtherAccordions(details, accordionDetails, summaryTop);
      });
    });
  };

  const measureWrapper = (wrapper) => {
    const content = wrapper.querySelector(contentSelector);
    const button = wrapper.querySelector(buttonSelector);
    if (!content || !button) return;

    const wasExpanded = wrapper.classList.contains('is-expanded');
    wrapper.classList.remove('is-overflowing');
    wrapper.classList.add('is-measuring');

    requestAnimationFrame(() => {
      const maxHeight = parseFloat(getComputedStyle(content).maxHeight);
      const isOverflowing = Number.isFinite(maxHeight) && content.scrollHeight > maxHeight + previewTolerance;
      wrapper.classList.toggle('is-overflowing', isOverflowing);
      wrapper.classList.toggle('is-expanded', isOverflowing && wasExpanded);
      wrapper.classList.remove('is-measuring');
      setButtonLabel(button, isOverflowing && wasExpanded);
    });
  };

  const measureAll = () => {
    document.querySelectorAll(wrapperSelector).forEach(measureWrapper);
  };

  const initWrapper = (wrapper) => {
    if (wrapper.dataset.pdpAccordionPreviewReady === 'true') return;
    const button = wrapper.querySelector(buttonSelector);
    if (!button) return;

    wrapper.dataset.pdpAccordionPreviewReady = 'true';
    setButtonLabel(button, false);

    button.addEventListener('click', () => {
      const expanded = !wrapper.classList.contains('is-expanded');
      wrapper.classList.toggle('is-expanded', expanded);
      setButtonLabel(button, expanded);
      if (!expanded) {
        scrollCollapsedWrapperIntoView(wrapper);
      }
    });

    const details = wrapper.closest('.product__accordion')?.querySelector('.accordion__details');
    if (details) {
      details.addEventListener('toggle', () => {
        if (details.open) {
          measureWrapper(wrapper);
        } else {
          wrapper.classList.remove('is-expanded');
          setButtonLabel(button, false);
        }
      });
    }
  };

  const init = () => {
    document.querySelectorAll(singleOpenRootSelector).forEach(initSingleOpenAccordion);
    document.querySelectorAll(wrapperSelector).forEach((wrapper) => {
      initWrapper(wrapper);
      measureWrapper(wrapper);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Coalesce resize bursts. The existing per-wrapper measurement stays unchanged.
  let resizeFrame = null;
  const scheduleResizeMeasure = () => {
    if (resizeFrame !== null) return;
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = null;
      measureAll();
    });
  };
  window.addEventListener('resize', scheduleResizeMeasure);
  window.addEventListener('load', measureAll);
  if (mobileQuery.addEventListener) {
    mobileQuery.addEventListener('change', measureAll);
  } else {
    mobileQuery.addListener(measureAll);
  }
  document.addEventListener('shopify:section:load', init);
})();
